import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  CheckSquare, 
  AlertTriangle, 
  Building2,
  Briefcase,
  MessageSquare,
  ArrowRight,
  Calendar
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { API_BASE_URL } from '../lib/api';

interface DashboardProps {
  user: { name: string; role: string } | null;
}

interface DashboardStats {
  totalAccounts: number;
  activeDeals: number;
  thisMonthRevenue: number;
  upcomingTodos: Array<{
    id: string;
    todoTitle: string;
    todoDueDate: string;
    daysLeft: number;
    linkType: string;
  }>;
  riskDeals: Array<{
    id: string;
    dealName: string;
    accountName: string;
    riskFlags: string[];
    winProb: number;
  }>;
  recentActivities: Array<{
    id: string;
    activityTitle: string;
    accountName: string;
    activityDatetime: string;
  }>;
}

export function Dashboard({ user }: DashboardProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/dashboard`, {
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error('データの取得に失敗しました');
        }
        
        const data = await response.json();
        setStats(data);
      } catch (err: any) {
        console.error('Dashboard fetch error:', err);
        setError(err.message);
        // エラー時はデフォルト値を設定
        setStats({
          totalAccounts: 0,
          activeDeals: 0,
          thisMonthRevenue: 0,
          upcomingTodos: [],
          riskDeals: [],
          recentActivities: [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { 
      style: 'currency', 
      currency: 'JPY',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ja-JP', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg" style={{ color: 'var(--text-secondary)' }}>読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          ダッシュボード
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          おかえりなさい、{user?.name}さん
        </p>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-700">
          {error}
        </div>
      )}

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
            <Building2 className="text-blue-600" size={24} />
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>取引先企業</p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {stats?.totalAccounts.toLocaleString()}
            </p>
          </div>
        </Card>

        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
            <Briefcase className="text-green-600" size={24} />
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>進行中の案件</p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {stats?.activeDeals.toLocaleString()}
            </p>
          </div>
        </Card>

        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
            <TrendingUp className="text-purple-600" size={24} />
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>今月の受注予定</p>
            <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {formatCurrency(stats?.thisMonthRevenue || 0)}
            </p>
          </div>
        </Card>
      </div>

      {/* コンテンツ行 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 期限間近のToDo */}
        <Card padding="none">
          <CardHeader className="flex flex-row items-center justify-between p-4">
            <CardTitle className="flex items-center gap-2">
              <CheckSquare size={20} className="text-blue-600" />
              期限間近のToDo
            </CardTitle>
            <Link 
              to="/todos" 
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              すべて見る <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.upcomingTodos.length === 0 ? (
              <p className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                期限間近のToDoはありません
              </p>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {stats?.upcomingTodos.map((todo) => (
                  <div key={todo.id} className="flex items-center justify-between p-4">
                    <div>
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {todo.todoTitle}
                      </p>
                      <p className="text-sm flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                        <Calendar size={14} />
                        {formatDate(todo.todoDueDate)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      todo.daysLeft <= 1 
                        ? 'bg-red-100 text-red-700' 
                        : todo.daysLeft <= 3 
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}>
                      {todo.daysLeft <= 0 ? '期限超過' : `あと${todo.daysLeft}日`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* リスク案件 */}
        <Card padding="none">
          <CardHeader className="flex flex-row items-center justify-between p-4">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-orange-500" />
              リスク案件
            </CardTitle>
            <Link 
              to="/deals?filter=risk" 
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              すべて見る <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.riskDeals.length === 0 ? (
              <p className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                リスク案件はありません
              </p>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {stats?.riskDeals.map((deal) => (
                  <Link 
                    key={deal.id} 
                    to={`/deals/${deal.id}`}
                    className="block p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {deal.dealName}
                        </p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {deal.accountName}
                        </p>
                        <div className="flex gap-1 mt-1">
                          {deal.riskFlags?.map((flag, i) => (
                            <span 
                              key={i}
                              className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700"
                            >
                              {flag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-lg font-bold ${
                          deal.winProb < 40 ? 'text-red-500' : 'text-yellow-500'
                        }`}>
                          {deal.winProb}%
                        </span>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          受注確度
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 最近の商談 */}
        <Card padding="none" className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between p-4">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare size={20} className="text-green-600" />
              最近の商談
            </CardTitle>
            <Link 
              to="/activities" 
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              すべて見る <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recentActivities.length === 0 ? (
              <p className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                商談履歴がありません
              </p>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {stats?.recentActivities.map((activity) => (
                  <Link 
                    key={activity.id}
                    to={`/activities/${activity.id}`}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div>
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {activity.activityTitle || '(タイトルなし)'}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {activity.accountName}
                      </p>
                    </div>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {activity.activityDatetime 
                        ? new Date(activity.activityDatetime).toLocaleDateString('ja-JP')
                        : '-'
                      }
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
